import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency, formatNumber } from "@/utils/format";
import { CustoPorUnidadeConstrutivaStat } from "../chartUtils";
import { BAR_HOVER_PROPS, TOOLTIP_CURSOR_BAND } from "@/utils/rechartsInteraction";

interface TooltipProps {
  active?:  boolean;
  payload?: { value: number; payload: CustoPorUnidadeConstrutivaStat }[];
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[220px]">
      <p className="text-xs font-semibold text-slate-700 leading-snug mb-2">{d.name}</p>
      <div className="flex flex-col gap-1 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Custo total (escopo)</span>
          <span className="font-bold text-rose-600">{formatCurrency(d.totalCost, true)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Parcelas</span>
          <span className="font-semibold text-slate-800">{formatNumber(d.parcelCount)}</span>
        </div>
      </div>
    </div>
  );
}

interface Props {
  data:      CustoPorUnidadeConstrutivaStat[];
  loading:   boolean;
  avgTotal:  number;
}

const COLORS_BELOW = "#10b981";
const COLORS_ABOVE = "#f43f5e";

export function CustoPorUnidadeConstrutiva({ data, loading, avgTotal }: Props) {
  const chartData = data.slice(0, 16);
  const dynamicHeight = Math.max(280, chartData.length * 42);

  return (
    <ChartContainer
      title="Custo por unidade construtiva"
      subtitle="Investimento agregado por etapa (unidade construtiva), como no Orçamento"
      loading={loading}
      empty={!loading && data.length === 0}
      height={dynamicHeight}
    >
      <BarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 4, right: 20, left: 8, bottom: 4 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v: number) =>
            v >= 1_000_000
              ? `R$${(v / 1_000_000).toFixed(1)}M`
              : `R$${(v / 1_000).toFixed(0)}K`
          }
        />
        <YAxis
          type="category"
          dataKey="name"
          width={180}
          tick={{ fontSize: 11, fill: "#475569" }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v: string) => (v.length > 26 ? v.slice(0, 26) + "…" : v)}
        />
        <Tooltip content={<CustomTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
        <Bar
          {...BAR_HOVER_PROPS}
          dataKey="totalCost"
          name="Custo total"
          radius={[0, 4, 4, 0]}
          maxBarSize={28}
        >
          {chartData.map((entry) => (
            <Cell
              key={entry.buildingUnitKey}
              fill={entry.totalCost > avgTotal ? COLORS_ABOVE : COLORS_BELOW}
            />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}
